import { useState, useEffect } from "react";
import { toast } from "sonner";
import { getAssetPublicUrl } from "@/utils/image-utils";
import { uploadImageAndCreatePreview, updatePreviewImage } from "@/services/preview-service";
import { CropSettings } from "@/lib/types";

interface UseImageUploadOptions {
  modelUuid: string;
  targetRatio?: number;
  currentAssetPath?: string;
  previewId?: string;
  subPreviewId?: string | null;
}

export function useImageUpload({
  modelUuid,
  targetRatio,
  currentAssetPath,
  previewId,
  subPreviewId = null,
}: UseImageUploadOptions) {
  const [open, setOpen] = useState(false);
  const [image, setImage] = useState<File | null>(null);
  const [originalImage, setOriginalImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [cropSettings, setCropSettings] = useState<CropSettings | null>(null);

  const handleImageSelected = async (file: File, previewUrl: string, cropSettings: CropSettings) => {
    setImage(file);
    setOriginalImage(file); // Store the original file
    setPreviewUrl(previewUrl);
    console.log("handleImageSelected cropSettings", cropSettings);
    setCropSettings(cropSettings || null);
  };

  const handleImageCleared = () => {
    setImage(null);
    setOriginalImage(null);
    setPreviewUrl(null);
    setCropSettings(null);
  };

  const handleUpload = async (projectId?: string | null) => {
    if (!originalImage) return null;

    setIsUploading(true);
    try {
      if (previewId && currentAssetPath) {
        // Update existing preview with original image + crop settings
        const result = await updatePreviewImage(
          originalImage,
          previewId,
          currentAssetPath,
          cropSettings || undefined
        );
        console.log("handleUpload cropSettings", cropSettings);
        if (!result.success) {
          throw new Error(result.error || "Failed to update preview");
        }

        toast("Success", {
          description: "Preview image updated successfully"
        });
        
        return previewId;
      } else {
        // Create new preview with original image + crop settings
        const result = await uploadImageAndCreatePreview(
          originalImage,
          modelUuid,
          {
            isSharable: false,
            subPreviewId: subPreviewId || undefined,
            projectId: projectId || undefined,
            cropSettings: cropSettings || undefined
          }
        );

        if (!result.success) {
          throw new Error(result.error || "Failed to create preview");
        }

        toast("Success", {
          description: "Label uploaded successfully",
        });
        
        return result.previewId;
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast("Error", {
        description: error instanceof Error ? error.message : "Failed to upload image",
      });
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  // Load existing image when editing
  useEffect(() => {
    if (previewId && currentAssetPath && !image) {
      const publicUrl = getAssetPublicUrl(currentAssetPath);
      setPreviewUrl(publicUrl);
    }
  }, [previewId, currentAssetPath, targetRatio, image]);

  return {
    open,
    setOpen,
    image,
    originalImage,
    previewUrl,
    isUploading,
    cropSettings,
    handleImageSelected,
    handleImageCleared,
    handleUpload
  };
}
